import React, { useCallback, useEffect, useRef } from 'react';

import { atom, useAtom, useAtomValue, useSetAtom } from 'jotai';
import { FormattedMessage } from 'react-intl';

import Button from '@atlaskit/button/default/button';
import RovoIcon from '@atlaskit/icon-lab/core/rovo';

import { useAtlassianUserContext } from '../../../../hooks/use-atlassian-user';
import { useAsideController, type Message } from '../../../contexts/AsideContext';
import { CypherQueryAtom, SelectedSiteCloudIdAtom } from '../../../state/LabsAtoms';
import { QueryBuilderSelectedEditorTabAtom } from '../../../state/QueryBuilderAtoms';
import { GraphExplorerChatAside } from '../../graph-explorer/home/ObjectsVisualizer/cyphie-chat';
import { messages } from '../../graph-explorer/home/ObjectsVisualizer/cyphie-chat/messages';

const owner = 'query-builder-assistant';
const queryAssistantHistoryAtom = atom<Record<string, Message[]>>({});

type QueryAssistantProps = {
	onUseCypher: (query: string) => void;
};

const QueryAssistantAside = ({ onUseCypher }: QueryAssistantProps): JSX.Element | null => {
	const cloudId = useAtomValue(SelectedSiteCloudIdAtom);
	const [histories, setHistories] = useAtom(queryAssistantHistoryAtom);
	const addMessage = useCallback(
		(message: Message) => {
			setHistories((previous) => ({
				...previous,
				[cloudId]: [...(previous[cloudId] ?? []), message],
			}));
		},
		[cloudId, setHistories],
	);
	const { identity, loading } = useAtlassianUserContext();
	if (loading || !identity.isAtlassianEmployee || !cloudId) {
		return null;
	}
	return (
		<GraphExplorerChatAside
			key={cloudId}
			external
			onUseCypher={onUseCypher}
			history={{ messages: histories[cloudId] ?? [], addMessage }}
		/>
	);
};

export const QueryAssistantButton = (): JSX.Element | null => {
	const setCypherQuery = useSetAtom(CypherQueryAtom);
	const setSelectedTab = useSetAtom(QueryBuilderSelectedEditorTabAtom);
	const onUseCypher = useCallback(
		(query: string) => {
			setCypherQuery(query);
			setSelectedTab(0);
		},
		[setCypherQuery, setSelectedTab],
	);
	const { identity, loading } = useAtlassianUserContext();
	const cloudId = useAtomValue(SelectedSiteCloudIdAtom);
	const aside = useAsideController();
	const asideRef = useRef(aside);
	asideRef.current = aside;

	useEffect(() => () => asideRef.current.closeIfOwned(owner), []);

	if (loading || !identity.isAtlassianEmployee) {
		return null;
	}

	return (
		<Button
			appearance="subtle"
			iconBefore={RovoIcon}
			isDisabled={!cloudId}
			isSelected={aside.isOpen && aside.owner === owner}
			aria-expanded={aside.isOpen && aside.owner === owner}
			onClick={() =>
				aside.toggle({
					owner,
					content: <QueryAssistantAside onUseCypher={onUseCypher} />,
				})
			}
		>
			<FormattedMessage {...messages.queryAssistantHeading} />
		</Button>
	);
};
